import styled, { css } from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../common/formInputsV2/styles/tokens';

// v2 look for the completed screens (Auto first). Kept apart from
// completedScreens.styles.js on purpose: the base styles there (.info-card,
// .card-header-custom, ...) are still shared by the screens that have not been
// migrated yet.
//
// The *Css fragments below style an element itself (they use `&`), so they are
// used both by the styled components of this file and, nested under the old
// class names (`.card-header-custom { ${fragment} }`), by the shared components
// that opt in with a `v2` prop.

export const cardV2Css = css`
  overflow: hidden;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.primaryBorder};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
  font-family: ${v2FontFamily};
  color: ${v2Colors.text};
`;

export const headerV2Css = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 12px 20px;
  overflow: hidden;
  background:
    radial-gradient(circle at 92% 0%, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0) 45%),
    linear-gradient(135deg, #0fbf6a 0%, ${v2Colors.primary} 45%, #007a3d 100%);
  color: #ffffff;

  /* decorative rings, purely visual */
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    border: 1.5px solid rgba(255, 255, 255, 0.18);
    pointer-events: none;
  }

  &::before {
    top: -48px;
    right: -22px;
    width: 120px;
    height: 120px;
  }

  &::after {
    top: -16px;
    right: 54px;
    width: 58px;
    height: 58px;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 10px 16px;
  }
`;

export const headerIconV2Css = css`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 6px rgba(0, 60, 30, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.35);
  font-size: 16px;
  color: #ffffff;
`;

export const headerTitleV2Css = css`
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: ${v2FontFamily};
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 60, 30, 0.25);
`;

export const sectionTitleV2Css = css`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${v2Colors.primaryBorder};
  font-family: ${v2FontFamily};

  h2 {
    margin: 0;
    font-family: ${v2FontFamily};
    font-size: 19px;
    font-weight: 700;
    color: ${v2Colors.text};
  }

  svg.section-icon {
    flex-shrink: 0;
    box-sizing: border-box;
    width: 34px;
    height: 34px;
    padding: 9px;
    border-radius: 10px;
    background: ${v2Colors.primarySoft};
    font-size: 16px;
    color: ${v2Colors.primaryHover};
  }
`;

// Label-over-value grid cells ("Informazioni Polizza" cards).
export const fieldGridV2Css = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
  margin: 0;
  padding: 18px 20px;

  @media (max-width: ${v2Breakpoints.tablet}) {
    gap: 12px 16px;
    padding: 14px 16px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Document rows (the "Documenti" list card).
export const documentListV2Css = css`
  .documents-body {
    padding: 0;
  }

  .documents-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .document-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding: 12px 18px;
    border-bottom: 1px solid #eaecf0;
    transition: background-color 150ms ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: ${v2Colors.surface};
    }
  }

  .document-content {
    flex: 1;
    min-width: 0;
    /* the base screen styles set a 1rem gap here; the compact lists (Tutela, ...) have none */
    gap: 0 !important;
  }

  .document-name {
    margin: 0 0 6px;
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.4;
    color: ${v2Colors.text};
  }

  .document-actions {
    flex-direction: row !important;
    align-items: center;
    gap: 12px;
  }

  .document-actions .file-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    img {
      width: 18px;
      height: auto;
    }

    .file-name {
      max-width: 260px;
      font-size: 12.5px;
      color: ${v2Colors.textMuted};
    }
  }

  .document-actions .doc-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 150ms ease;

    img {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background: ${v2Colors.primarySoftHover};
    }
  }

  .document-status {
    flex-shrink: 0;
    margin-left: 0;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;

    &.status-warning {
      border-color: ${v2Colors.warningBorder};
      background: ${v2Colors.warningSoft};
      color: ${v2Colors.warningText};
    }

    &.status-danger {
      border-color: ${v2Colors.dangerBorder};
      background: ${v2Colors.dangerSoft};
      color: ${v2Colors.danger};
    }

    &.status-success {
      border-color: ${v2Colors.primaryBorder};
      background: ${v2Colors.primarySoft};
      color: ${v2Colors.primaryHover};
    }

    &.status-info {
      border-color: ${v2Colors.infoBorder};
      background: ${v2Colors.infoSoft};
      color: ${v2Colors.info};
    }
  }

  /* older bootstrap list-group document lists (Assistenza Stradale, Tutela Legale, ...) */
  .list-group {
    margin: 0;
    padding: 0;
    border-radius: 0;
  }

  .list-group-item {
    padding: 12px 18px;
    border: 0;
    border-bottom: 1px solid #eaecf0;
    border-radius: 0;
    background: transparent;
    font-family: ${v2FontFamily};
    color: ${v2Colors.text};
    transition: background-color 150ms ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: ${v2Colors.surface};
    }

    /* same sizes as the document-item list above */
    .ms-2 {
      margin-left: 0 !important;
    }

    .fw-bold {
      margin-bottom: 6px !important;
      font-size: 13.5px;
      font-weight: 600 !important;
      line-height: 1.4;
      color: ${v2Colors.text};
    }

    h5 {
      font-size: 12.5px;
      font-weight: 500;
      color: ${v2Colors.textMuted};
    }

    img.img-fluid {
      width: 18px;
      height: auto;
    }

    .ms-2 .justify-content-between {
      justify-content: flex-start !important;
      gap: 12px;
    }

    .ms-2 .justify-content-between .mx-2 {
      margin-right: 0 !important;
      margin-left: 0 !important;
    }

    .doc-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      padding: 0;
      border-radius: 50%;
      cursor: pointer;
      transition: background-color 150ms ease;

      img {
        width: 16px;
        height: 16px;
      }

      &:hover {
        background: ${v2Colors.primarySoftHover};
      }
    }

    .badge {
      display: inline-flex;
      align-items: center;
      padding: 3px 10px;
      border: 1px solid transparent;
      font-size: 11.5px;
      font-weight: 600;
      line-height: 1.5;
    }

    .badge.bg-primary {
      border-color: ${v2Colors.primaryBorder};
      background: ${v2Colors.primarySoft} !important;
      color: ${v2Colors.primaryHover};
    }

    .badge.bg-warning {
      border-color: ${v2Colors.warningBorder};
      background: ${v2Colors.warningSoft} !important;
      color: ${v2Colors.warningText} !important;
    }

    .badge.bg-danger {
      border-color: ${v2Colors.dangerBorder};
      background: ${v2Colors.dangerSoft} !important;
      color: ${v2Colors.danger};
    }

    .badge.bg-info {
      border-color: ${v2Colors.infoBorder};
      background: ${v2Colors.infoSoft} !important;
      color: ${v2Colors.info} !important;
    }
  }

  /* no 1. 2. 3. counters on the numbered lists, same as the other document lists */
  .list-group-numbered > .list-group-item::before {
    content: none;
  }

  .document-motivation {
    margin: 4px 0 0;
    font-size: 13px;
    color: ${v2Colors.danger};
  }

  /* phones: name + status badge on the first row, file + download below */
  @media (max-width: 576px) {
    .document-item {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: start;
      gap: 10px 12px;
      padding: 14px 16px;
    }

    .document-content {
      display: contents;
    }

    .document-name {
      grid-column: 1;
      grid-row: 1;
      align-self: center;
      line-height: 1.3;
    }

    .document-status {
      grid-column: 2;
      grid-row: 1;
      align-self: center;
    }

    .document-actions {
      grid-column: 1 / -1;
      grid-row: 2;
      padding: 8px 8px 8px 12px;
      border: 1px solid #eaecf0;
      border-radius: 12px;
      background: ${v2Colors.surface};
    }

    .document-actions .file-info {
      flex: 1;
    }

    .document-actions .file-info .file-name {
      flex: 1;
      min-width: 0;
      max-width: none;
    }

    .document-actions .doc-btn {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
    }

    .document-motivation {
      grid-column: 1 / -1;
      margin: 0;
    }
  }
`;

// Documenti list redesign (PDF tile + round download button), used by every completed screen.
export const docsRedesignCss = css`
  .document-item {
    align-items: center;
    padding: 14px 18px;
  }

  /* same look for the older bootstrap list-group lists */
  .list-group-item {
    align-items: center !important;
    padding: 14px 18px;

    /* status badge: in the row and vertically centered on the right, like the document-item lists */
    .badge.position-absolute {
      position: static !important;
      margin: 0 !important;
    }

    .fw-bold {
      font-size: 14px;
    }

    h5 {
      font-size: 13px;
    }

    img.img-fluid {
      box-sizing: border-box;
      width: 34px;
      height: 34px;
      padding: 7px;
      border: 1px solid #fbd9d9;
      border-radius: 10px;
      background: #fef2f2;
    }

    .doc-btn {
      width: 32px;
      height: 32px;
      border: 1px solid ${v2Colors.primaryBorder} !important;
      background: ${v2Colors.primarySoft} !important;

      img {
        width: 16px;
        height: 16px;
        transition: filter 150ms ease;
      }

      &:hover {
        background: ${v2Colors.primary} !important;
        border-color: ${v2Colors.primary} !important;

        img {
          filter: brightness(0) invert(1);
        }
      }
    }
  }

  .document-name {
    font-size: 14px;
  }

  .document-actions .file-info {
    gap: 12px;

    /* the PDF icon sits in a soft red tile */
    img {
      box-sizing: border-box;
      width: 34px;
      height: 34px;
      padding: 7px;
      border: 1px solid #fbd9d9;
      border-radius: 10px;
      background: #fef2f2;
    }

    .file-name {
      font-size: 13px;
    }
  }

  /* round soft-green download button that fills on hover */
  .document-actions .doc-btn {
    width: 32px;
    height: 32px;
    border: 1px solid ${v2Colors.primaryBorder} !important;
    background: ${v2Colors.primarySoft} !important;

    img {
      width: 16px;
      height: 16px;
      transition: filter 150ms ease;
    }

    &:hover {
      background: ${v2Colors.primary} !important;
      border-color: ${v2Colors.primary} !important;

      img {
        filter: brightness(0) invert(1);
      }
    }
  }
`;

export const CompletedCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  ${cardV2Css}
  ${documentListV2Css}
  ${docsRedesignCss}
`;

export const CompletedCardHeader = styled.div`
  ${headerV2Css}
`;

export const CompletedCardIcon = styled.div`
  ${headerIconV2Css}
`;

export const CompletedCardTitle = styled.h3`
  ${headerTitleV2Css}
`;

export const CompletedSectionTitle = styled.div`
  ${sectionTitleV2Css}
`;

export const CompletedFieldGrid = styled.dl`
  ${fieldGridV2Css}
`;

export const CompletedField = styled.div`
  min-width: 0;

  &.full {
    grid-column: 1 / -1;
  }

  dt {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${v2Colors.textMuted};
  }

  dd {
    margin: 0;
    min-height: 1.3em;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: ${v2Colors.text};
    overflow-wrap: anywhere;
  }

  /* highlighted amount row ("Totale") */
  &.total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    border: 1px solid ${v2Colors.primaryBorder};
    border-radius: 12px;
    background: ${v2Colors.primarySoft};

    dt {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
      color: ${v2Colors.primaryHover};
    }

    dd {
      font-size: 18px;
      font-weight: 700;
      color: ${v2Colors.primaryHover};
      font-variant-numeric: tabular-nums;
    }
  }
`;

// Opt-in (v2 prop) overrides for ContractDocuments.jsx, nested in its wrapper.
export const contractDocumentsV2Css = css`
  .contract-card {
    ${cardV2Css}
    transition: none;

    &:hover {
      box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
    }
  }

  .card-header-custom {
    ${headerV2Css}
    justify-content: space-between;
    flex-wrap: wrap;
    border: none;

    .header-left {
      display: flex;
      align-items: center;
      gap: 13px;
    }

    .header-icon {
      ${headerIconV2Css}
    }

    .header-title {
      ${headerTitleV2Css}
    }

    .signature-link {
      gap: 16px;

      a {
        font-size: 14px;
        font-weight: 600;
        border-bottom-color: rgba(255, 255, 255, 0.7);
      }

      .copy-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        padding: 0;
        border: 1px solid rgba(255, 255, 255, 0.4);
        border-radius: 9px;
      }
    }

    .action-button {
      padding: 7px 14px;
      border: 1px solid rgba(255, 255, 255, 0.55);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.95);
      font-size: 13px;
      font-weight: 700;
      color: ${v2Colors.primaryHover};
      box-shadow: 0 2px 6px rgba(0, 60, 30, 0.18);

      &:hover {
        background: #ffffff;
        transform: translateY(-1px);
        box-shadow: 0 4px 10px rgba(0, 60, 30, 0.22);
      }
    }

    .signed-badge {
      padding: 5px 12px;
      border: 1px solid rgba(255, 255, 255, 0.45);
      border-radius: 999px;
      font-size: 13px;
      font-weight: 600;
    }
  }

  .documents-grid {
    gap: 16px;
    padding: 18px 20px;
  }

  .document-card {
    width: 304px;
    padding: 16px;
    gap: 14px;
    border: 1px solid #eaecf0;
    border-radius: 14px;
    background: ${v2Colors.inputBackground};

    &:hover {
      border-color: ${v2Colors.primaryBorder};
      box-shadow: 0 4px 14px rgba(0, 166, 81, 0.12);
    }
  }

  .document-type-label {
    max-width: 160px;
    padding: 4px 11px;
    border: 1px solid #eaecf0;
    border-radius: 999px;
    background: ${v2Colors.surface};
    font-size: 12px;
    font-weight: 600;
    color: ${v2Colors.textMuted};
  }

  .status-badge {
    padding: 4px 11px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 700;

    &.status-valid {
      border-color: ${v2Colors.primaryBorder};
      background: ${v2Colors.primarySoft};
      color: ${v2Colors.primaryHover};
    }

    &.status-invalid {
      border-color: ${v2Colors.dangerBorder};
      background: ${v2Colors.dangerSoft};
      color: ${v2Colors.danger};
    }

    &.status-pending {
      border-color: ${v2Colors.warningBorder};
      background: ${v2Colors.warningSoft};
      color: ${v2Colors.warningText};
    }

    &.status-not-required {
      border-color: #eaecf0;
      background: ${v2Colors.surface};
      color: ${v2Colors.textMuted};
    }
  }

  .document-content {
    min-height: 0;
    margin-bottom: 10px;
    gap: 12px;

    .pdf-icon {
      width: 42px;
      height: 42px;
      border-radius: 12px;
      font-size: 19px;
    }

    .document-name {
      font-size: 13.5px;
      font-weight: 600;
      color: ${v2Colors.text};
    }
  }

  .document-actions {
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 6px;
    padding-top: 20px;
    border-top: 1px solid #eaecf0;

    .btn-download,
    .btn-upload {
      min-width: 0;
      gap: 8px;
      padding: 10px 14px;
      border-radius: 11px;
      font-size: 13.5px;
      font-weight: 700;
    }

    .btn-download {
      background: ${v2Colors.primary};

      &:hover {
        background: ${v2Colors.primaryHover};
        box-shadow: 0 4px 10px rgba(0, 166, 81, 0.25);
      }
    }

    .btn-upload {
      background: ${v2Colors.warning};

      &:hover {
        background: ${v2Colors.warningHover};
        box-shadow: 0 4px 10px rgba(245, 158, 11, 0.25);
      }
    }
  }
`;

// Opt-in (v2 prop) overrides for CompletedQuoteScreenAutoAfterSale.jsx.
export const afterSaleV2Css = css`
  .section-title {
    ${sectionTitleV2Css}
  }

  .info-card {
    ${cardV2Css}
    transition: none;

    &:hover {
      box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
    }
  }

  .card-header-custom {
    ${headerV2Css}
    border: none;

    .header-icon {
      ${headerIconV2Css}
    }

    .header-title {
      ${headerTitleV2Css}
    }
  }

  .options-grid {
    gap: 14px;
    padding: 18px 20px;
  }

  .option-card {
    gap: 14px;
    padding: 14px 16px;
    border: 1px solid #eaecf0;
    border-radius: 14px;

    &:hover {
      background: ${v2Colors.primarySoft};
      border-color: ${v2Colors.primaryBorder};
      box-shadow: 0 4px 14px rgba(0, 166, 81, 0.12);
      transform: translateY(-1px);
    }

    .option-icon {
      width: 38px;
      height: 38px;
      border-radius: 11px;
      background: linear-gradient(135deg, #0fbf6a 0%, ${v2Colors.primary} 60%, #007a3d 100%);
    }

    .option-content {
      .option-title {
        font-family: ${v2FontFamily};
        font-size: 14px;
        font-weight: 700;
        color: ${v2Colors.text};
      }

      .option-description {
        display: block;
        margin-top: 3px;
        font-family: ${v2FontFamily};
        font-size: 12.5px;
        color: ${v2Colors.textMuted};
      }
    }

    &:hover .option-arrow {
      color: ${v2Colors.primaryHover};
    }
  }
`;

// Opt-in (v2 prop) overrides for AfterSaleInfoCards.jsx. The per-type header
// colours (red/orange/...) are kept on purpose: they tell the types apart.
export const afterSaleCardsV2Css = css`
  gap: 16px;

  .after-sale-card {
    border: 1px solid ${v2Colors.primaryBorder};
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
    font-family: ${v2FontFamily};

    &:hover {
      transform: none;
      box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
    }
  }

  .card-header-modern {
    gap: 13px;
    padding: 12px 20px;

    &::before {
      top: -48px;
      right: -22px;
      width: 120px;
      height: 120px;
      border: 1.5px solid rgba(255, 255, 255, 0.18);
      background: none;
      transform: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: -16px;
      right: 54px;
      width: 58px;
      height: 58px;
      border: 1.5px solid rgba(255, 255, 255, 0.18);
      border-radius: 50%;
      pointer-events: none;
    }

    .header-icon {
      ${headerIconV2Css}
    }

    .header-title {
      ${headerTitleV2Css}
    }

    .header-count {
      padding: 4px 12px;
      border: 1px solid rgba(255, 255, 255, 0.4);
      border-radius: 999px;
      font-size: 12px;
    }
  }

  .modern-table {
    thead {
      background: ${v2Colors.surface};

      th {
        padding: 10px 14px;
        border-bottom: 1px solid #eaecf0;
        font-size: 11px;
        letter-spacing: 0.06em;
        color: ${v2Colors.textMuted};

        &:first-child {
          padding-left: 18px;
        }

        &:last-child {
          padding-right: 18px;
        }
      }
    }

    tbody tr {
      &:hover {
        background: ${v2Colors.surface};
        box-shadow: inset 3px 0 0 ${v2Colors.primary};
      }

      td {
        padding: 10px 14px;
        font-size: 14px;

        &:first-child {
          padding-left: 18px;
        }

        &:last-child {
          padding-right: 18px;
        }
      }
    }
  }

  .status-badge {
    padding: 4px 11px;
    border-radius: 999px;
    font-size: 12px;
  }

  .action-button {
    padding: 7px 14px;
    border-radius: 999px;
    background: ${v2Colors.primary};
    font-size: 13px;
    box-shadow: none;

    &:hover {
      background: ${v2Colors.primaryHover};
      box-shadow: 0 4px 10px rgba(0, 166, 81, 0.25);
      transform: none;
    }
  }
`;
